import {
  DropdownButton,
  DropdownButtonContext,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ConversationLanguageCode } from 'lib-shared-languages';
import { ampli } from 'lib-shared-tracking';
import { AsyncStatus } from 'lib-shared-types-api';
import { useEffect, useMemo, useState } from 'react';

import { isDetailedError } from 'apis/ai/types/aiApi.types';
import {
  TranslateTextLanguageSelectionList,
  TranslateTextLanguageSelectionListProps,
} from 'components/translateTextLanguageSelectionList';
import {
  languageCodesToSelectionListValue,
  LanguageSelectionListValue,
} from 'utils/useLanguageSelectionList';
import { useTranslateText } from 'utils/useTranslateText';

/** The status of translation. */
export type TranslationStatus = AsyncStatus | 'notNeeded';

type LanguageSelection = {
  code: ConversationLanguageCode;
  source: 'conversation' | 'message';
};

export type TranslateTextChangeData = {
  /** The selected language. */
  readonly selectedLanguage?: LanguageSelectionListValue;
  /** The original untranslated text. */
  readonly originalText: string;
  /** The translated text; */
  readonly translatedText?: string;
  /** The feedback id from the endpoint that produced the translation. */
  readonly feedbackId?: string;
  /** The status of the translation. */
  readonly status: TranslationStatus;
  /** The error detail code */
  readonly errorDetailCode?: string;
};

type TranslateTextDropdownSelectionListProps = {
  /** The message to translate. */
  message: string;
  /** The selected language code to which the text should be translated to. */
  selectedLanguageCode?: ConversationLanguageCode;
  /** The original language of the text. */
  originalLanguageCode: ConversationLanguageCode | null;
  /** The callback for change of translation. */
  onChange?: (event: TranslateTextChangeData) => void;
  /** The csid */
  csid?: string;
  /** The content type of the text (text or html). */
  contentType?: 'text' | 'html';
} & Pick<TranslateTextLanguageSelectionListProps, 'onRevert'>;

/**
 * Represents a dropdown component for translating given text to a selected
 * language.
 */
export const TranslateTextDropdownSelectionList = (
  props: TranslateTextDropdownSelectionListProps,
): JSX.Element => {
  const {
    message,
    originalLanguageCode,
    onChange,
    onRevert,
    csid,
    contentType,
  } = props;
  const [languageSelection, setLanguageSelection] = useState<
    LanguageSelection | undefined
  >(
    props.selectedLanguageCode
      ? { code: props.selectedLanguageCode, source: 'conversation' }
      : undefined,
  );

  useEffect(() => {
    setLanguageSelection(
      props.selectedLanguageCode
        ? { code: props.selectedLanguageCode, source: 'conversation' }
        : undefined,
    );
  }, [props.selectedLanguageCode]);

  const selectedLanguage = useMemo(() => {
    return languageSelection
      ? languageCodesToSelectionListValue([languageSelection.code])[0]
      : undefined;
  }, [languageSelection]);
  const [translate, translateResponse] = useTranslateText();
  const isTranslating = translateResponse.asyncStatus === 'pending';

  useEffect(() => {
    if (translateResponse.error) {
      return;
    }
    if (
      translateResponse.asyncStatus !== 'fulfilled' ||
      // When receiving a response, only change when selected language of the response is the same as the selected language
      // Otherwise we discard the response as a new one should have been requested
      // This fixes a race condition
      (translateResponse.asyncStatus === 'fulfilled' &&
        selectedLanguage &&
        selectedLanguage.name === translateResponse.originalArg?.language)
    ) {
      onChange?.({
        originalText: message,
        selectedLanguage,
        translatedText: translateResponse.translatedText,
        feedbackId: translateResponse.feedbackId,
        status: translateResponse.asyncStatus,
      });
    }
    // we only care about the changes in the response
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translateResponse]);

  // Handles translating and reverting.
  useEffect(() => {
    if (selectedLanguage && languageSelection) {
      // Only skip translation if translating the entire conversation
      // and the language matches. Individual message translations always call the API.
      if (
        languageSelection.source === 'conversation' &&
        languageSelection.code === originalLanguageCode
      ) {
        onChange?.({
          originalText: message,
          selectedLanguage,
          status: 'notNeeded',
        });
      } else {
        translate({
          language: selectedLanguage.name,
          text: message,
          csid,
          contentType,
        }).catch((error) => {
          if (isDetailedError(error)) {
            onChange?.({
              originalText: message,
              selectedLanguage,
              status: 'rejected',
              errorDetailCode: error.data.detail,
            });
          }

          console.warn('Could not translate: ', error);
        });
      }
    } else {
      onChange?.({
        originalText: message,
        status: 'uninitialized',
      });
    }
    // Only run when selected language changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const dropdown = useMemo(
    () => (
      <DropdownButtonContext.Consumer>
        {(dropdownProps) => (
          <TranslateTextLanguageSelectionList
            autoFocusSearch={dropdownProps.dropdownOpen}
            selectedLanguageCode={languageSelection?.code}
            onRevert={onRevert}
            onLanguageChange={(event) => {
              if (event.selectedLanguage) {
                ampli.agentCopilotTranslateMessageLanguageSelected({
                  language: event.selectedLanguage.name,
                  context: 'singleMessage',
                  ...(csid ? { csid } : {}),
                });
              }

              setLanguageSelection(
                event.selectedLanguage
                  ? { code: event.selectedLanguage.code, source: 'message' }
                  : undefined,
              );
              dropdownProps.closeDropdown();
            }}
          />
        )}
      </DropdownButtonContext.Consumer>
    ),
    [languageSelection?.code, onRevert],
  );

  return (
    <Tooltip content="Translate">
      <DropdownButton
        fixed
        noArrow
        icon="translate"
        buttonProps={{
          isLoading: isTranslating,
          disabled: isTranslating,
        }}
        dropdown={dropdown}
      />
    </Tooltip>
  );
};
